import { useEffect, useMemo, useRef, useState } from 'react';

import { Search } from 'icons';
import { useIntl } from 'localization';
import { ActionIcon, ActionIconSize, ActionIconTheme, TextInput } from 'ui/atoms';

import { ThresholdConfig, rateColor, rateSeverity } from '../../MapSharedData';
import { PostcodeMarker } from '../types';

interface Props {
  region: string;
  total: number;
  postcodes: PostcodeMarker[];
  cfg: ThresholdConfig;
  focusedPostcode: string | null;
  onSelect: (m: PostcodeMarker) => void;
  onClose: () => void;
}

/**
 * Sidebar listing all postcodes inside the active region, sorted by count
 * descending. Clicking a postcode triggers a fitBounds zoom on the map.
 *
 * Features:
 *  - Search box filters by postcode or place name (handy for US states with
 *    1000+ ZIP codes).
 *  - Keyboard navigation: ↑↓ move highlight, Enter selects, Escape clears the
 *    query or — when already empty — steps back out to the region view.
 *  - When the focused postcode changes from a map click, the matching row is
 *    scrolled into view to keep list and map in sync.
 */
export function PostcodeSidebar({
  region,
  total,
  postcodes,
  cfg,
  focusedPostcode,
  onSelect,
  onClose,
}: Props) {
  const { formatMessage } = useIntl();
  // Region rate denominator, summed from the postcodes we have (percent mode);
  // undefined in absolute mode so severity falls back to the raw total.
  const regionSent = postcodes.some((p) => p.sent != null)
    ? postcodes.reduce((s, p) => s + (p.sent ?? 0), 0)
    : undefined;
  const sev = rateSeverity(total, regionSent, cfg);

  const [query, setQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const listRef = useRef<HTMLDivElement | null>(null);

  // Autofocus the search input when this sidebar mounts (each time the user
  // opens a region) so keyboard nav works immediately without a click.
  const searchWrapRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    searchWrapRef.current?.querySelector('input')?.focus();
  }, []);

  // Filter by postcode OR place name (case-insensitive substring)
  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return postcodes;
    return postcodes.filter(
      (p) => p.postcode?.toLowerCase().includes(q) || p.place.toLowerCase().includes(q),
    );
  }, [postcodes, query]);

  // Reset highlight when the filtered set changes
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [query]);

  // Keep the highlighted row in view while arrowing
  useEffect(() => {
    if (highlightedIndex < 0 || !listRef.current) return;
    const el = listRef.current.querySelector<HTMLElement>(`[data-row-index="${highlightedIndex}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [highlightedIndex]);

  // Scroll the focused row into view when focusedPostcode changes from outside
  // (a map click). `nearest` avoids jarring jumps when it's already visible.
  const focusedRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    if (focusedPostcode && focusedRef.current) {
      focusedRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [focusedPostcode]);

  // Keyboard navigation. Escape has two-stage behavior: clear query first,
  // then (if already empty) step back out to the region via onClose.
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation(); // don't let the global Escape handler also fire
      if (query) {
        setQuery('');
      } else {
        onClose();
      }
      return;
    }
    if (rows.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((i) => (i + 1) % rows.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((i) => (i <= 0 ? rows.length - 1 : i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const idx = highlightedIndex >= 0 ? highlightedIndex : 0;
      const row = rows[idx];
      if (row) onSelect(row);
    }
  };

  return (
    <div className="w-[280px] bg-white border-l border-v2blueGray-200 flex flex-col overflow-hidden shrink-0">
      <div className="px-4 py-3.5 border-b border-v2blueGray-200">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[11px] text-v2blueGray-500 uppercase tracking-[0.06em]">
            {formatMessage({ id: 'analytics.incidentMap.region' })}
          </div>
          <ActionIcon
            onClick={onClose}
            size={ActionIconSize.xs}
            theme={ActionIconTheme.gray200}
            title={formatMessage({ id: 'analytics.incidentMap.close' })}
            className="text-base"
          >
            ✕
          </ActionIcon>
        </div>
        <div className="text-base font-semibold text-v2blueGray-900 mb-1.5">{region}</div>
        <div className="flex items-baseline gap-2">
          <span className="text-xl font-semibold" style={{ color: sev.color }}>
            {formatMessage({ id: 'analytics.incidentMap.ordersCount' }, { count: total })}
          </span>
          <span className="text-[11px]" style={{ color: sev.color }}>
            {formatMessage({ id: sev.labelId })}
          </span>
          <span className="text-[11px] text-v2blueGray-500 ml-auto">
            {formatMessage(
              { id: 'analytics.incidentMap.postcodesCount' },
              { count: postcodes.length },
            )}
          </span>
        </div>
      </div>

      {/* Search — filters by postcode or place name */}
      <div ref={searchWrapRef} className="px-3 py-2 border-b border-v2blueGray-200">
        <TextInput
          name="postcode-search"
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={formatMessage({ id: 'analytics.incidentMap.postcodes.searchPlaceholder' })}
          icon={<Search />}
          iconPosition="left"
        />
      </div>

      <div ref={listRef} className="flex-1 overflow-y-auto">
        {rows.length === 0 ? (
          <div className="p-6 text-center text-v2blueGray-500 text-[13px]">
            {query
              ? formatMessage({ id: 'analytics.incidentMap.postcodes.emptySearch' }, { query })
              : formatMessage({ id: 'analytics.incidentMap.postcodes.empty' })}
          </div>
        ) : (
          rows.map((p, index) => {
            const isFocused = p.postcode === focusedPostcode;
            const isHighlighted = index === highlightedIndex;
            const color = rateColor(p.count, p.sent, cfg);
            return (
              <button
                key={p.postcode}
                data-row-index={index}
                ref={isFocused ? focusedRef : undefined}
                onClick={() => onSelect(p)}
                onMouseEnter={() => setHighlightedIndex(index)}
                className={`flex gap-2.5 w-full px-4 py-2.5 border-none border-b border-v2blueGray-200 cursor-pointer text-left transition-colors duration-150 ${
                  isFocused && p.incidents && p.incidents.length > 0
                    ? 'items-start'
                    : 'items-center'
                } ${
                  isFocused
                    ? 'bg-primary/10 border-l-[3px] border-l-primary'
                    : isHighlighted
                    ? 'bg-v2blueGray-100 border-l-[3px] border-l-transparent'
                    : 'bg-transparent border-l-[3px] border-l-transparent hover:bg-v2blueGray-100'
                }`}
              >
                <div
                  className={`w-2.5 h-2.5 rounded-full border-[1.5px] border-white shrink-0 ${
                    isFocused && p.incidents && p.incidents.length > 0 ? 'mt-1' : ''
                  }`}
                  style={{ background: color, boxShadow: '0 0 0 1px rgba(31,60,78,0.1)' }}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-[13px] font-medium text-v2blueGray-900">
                      {p.postcode}
                    </span>
                    <span className="text-[13px] font-semibold whitespace-nowrap" style={{ color }}>
                      {formatMessage(
                        { id: 'analytics.incidentMap.ordersCount' },
                        { count: p.count },
                      )}
                    </span>
                  </div>
                  <div className="text-[11px] text-v2blueGray-600 overflow-hidden text-ellipsis whitespace-nowrap">
                    {p.place}
                  </div>
                  {isFocused && p.incidents && p.incidents.length > 0 && (
                    <div className="mt-2 pt-1 border-t border-v2blueGray-200/70 flex flex-col">
                      <div className="text-[10px] uppercase tracking-[0.06em] text-v2blueGray-500 mb-1">
                        {formatMessage({ id: 'analytics.incidentMap.ordersByType' })}
                      </div>
                      {[...p.incidents]
                        .sort((a, b) => b.count - a.count)
                        .map((inc) => (
                          <div
                            key={`${inc.code}-${inc.courier}`}
                            className="flex items-start justify-between gap-2 py-1.5 border-b border-v2blueGray-200/60 last:border-b-0"
                          >
                            <div className="min-w-0">
                              <div className="text-[11px] text-v2blueGray-700 break-words">
                                {formatMessage({
                                  id: `notifs.filters.exception-type.${inc.code.toLowerCase()}`,
                                  defaultMessage: inc.code,
                                })}
                              </div>
                              <div className="text-[10px] text-v2blueGray-500 mt-0.5">
                                {formatMessage({
                                  id: `general.courier.${inc.courier.toLowerCase()}`,
                                  defaultMessage: inc.courier,
                                })}
                              </div>
                            </div>
                            <span className="text-[11px] font-semibold text-v2blueGray-700 shrink-0 tabular-nums">
                              {inc.count}
                            </span>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              </button>
            );
          })
        )}
      </div>

      <div className="px-4 py-2 border-t border-v2blueGray-200 text-[11px] text-v2blueGray-500 italic">
        {formatMessage({ id: 'analytics.incidentMap.postcodes.footerHint' })}
      </div>
    </div>
  );
}
